import {
  ReadonlyStack,
  TwoColumnLayout,
} from '@issuemanager/apps/client/components';
import { IssueTypeEnum } from '@issuemanager/constants';
import { Stack, Typography } from '@mui/material';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  issueType?: string;
  title?: ReactNode;
  description?: ReactNode;
  severity: ReactNode;
  dueDate: ReactNode;
  govmapGroups: ReactNode;
  impact: ReactNode;
  ownerId: ReactNode;
  approverId?: ReactNode;
  managerId?: ReactNode;
  // Audit Finding fields
  reviewStatus?: ReactNode;
  datePublished?: ReactNode;
  classification?: ReactNode;
  resolution?: ReactNode;
  rootCause?: ReactNode;
  recommendations?: ReactNode;
  managementResponse?: ReactNode;
  // Control Deficiency fields
  remediation?: ReactNode;
};

type RemediationSectionProps = {
  issueType?: string;
  reviewStatus?: ReactNode;
  resolution?: ReactNode;
  managementResponse?: ReactNode;
  remediation?: ReactNode;
};

type BaseLayoutProps = {
  title?: ReactNode;
  severity: ReactNode;
  dueDate: ReactNode;
  govmapGroups: ReactNode;
  ownerId: ReactNode;
  approverId?: ReactNode;
  managerId?: ReactNode;
};

type DefaultLayoutProps = BaseLayoutProps;

type AuditFindingLayoutProps = BaseLayoutProps & {
  datePublished?: ReactNode;
  classification?: ReactNode;
};

type ControlDeficiencyLayoutProps = BaseLayoutProps & {
  classification?: ReactNode;
};

function DefaultLayout({
  title,
  severity,
  dueDate,
  govmapGroups,
  managerId,
  ownerId,
  approverId,
}: DefaultLayoutProps): React.JSX.Element {
  return (
    <TwoColumnLayout
      leftColumn={[title, severity, dueDate, govmapGroups]}
      rightColumn={[managerId, ownerId, approverId]}
    />
  );
}

function AuditFindingLayout({
  title,
  severity,
  managerId,
  approverId,
  govmapGroups,
  dueDate,
  datePublished,
  classification,
  ownerId,
}: AuditFindingLayoutProps): React.JSX.Element {
  return (
    <TwoColumnLayout
      leftColumn={[title, severity, classification, dueDate, datePublished]}
      rightColumn={[govmapGroups, managerId, approverId, ownerId]}
    />
  );
}

function ControlDeficiencyLayout({
  title,
  severity,
  managerId,
  approverId,
  govmapGroups,
  dueDate,
  classification,
  ownerId,
}: ControlDeficiencyLayoutProps): React.JSX.Element {
  return (
    <TwoColumnLayout
      leftColumn={[title, severity, classification, dueDate]}
      rightColumn={[govmapGroups, managerId, approverId, ownerId]}
    />
  );
}

export function RemediationSection({
  issueType,
  reviewStatus,
  resolution,
  managementResponse,
  remediation,
}: RemediationSectionProps): React.JSX.Element | null {
  const { t } = useTranslation('issue');
  const isAuditFinding = issueType === IssueTypeEnum.AUDIT_FINDING;
  const isControlDeficiency = issueType === IssueTypeEnum.CONTROL_DEFICIENCY;

  if (!isAuditFinding && !isControlDeficiency) {
    return null;
  }

  return (
    <Stack spacing={3}>
      <Typography variant='h3' fontWeight='medium'>
        {t('form.sections.remediation')}
      </Typography>

      <Stack spacing={3}>
        {isControlDeficiency && remediation}
        {managementResponse}
        {resolution}
        {isAuditFinding && reviewStatus}
      </Stack>
    </Stack>
  );
}

export function IssueFields({
  title,
  description,
  severity,
  dueDate,
  govmapGroups,
  impact,
  ownerId,
  approverId,
  managerId,
  issueType,
  datePublished,
  classification,
  rootCause,
  recommendations,
}: Props): React.JSX.Element {
  const { t } = useTranslation('issue');
  const isAuditFinding = issueType === IssueTypeEnum.AUDIT_FINDING;
  const isControlDeficiency = issueType === IssueTypeEnum.CONTROL_DEFICIENCY;

  const layoutProps = {
    title,
    severity,
    dueDate,
    govmapGroups,
    ownerId,
    approverId,
    managerId,
    datePublished,
    classification,
  };

  const renderLayout = (): React.JSX.Element => {
    if (isAuditFinding) {
      return <AuditFindingLayout {...layoutProps} />;
    }
    if (isControlDeficiency) {
      return <ControlDeficiencyLayout {...layoutProps} />;
    }
    return <DefaultLayout {...layoutProps} />;
  };

  return (
    <Stack data-testid='details' spacing={3}>
      <ReadonlyStack>{renderLayout()}</ReadonlyStack>

      <Typography variant='h3' fontWeight='medium'>
        {t('form.sections.analysis')}
      </Typography>

      <Stack spacing={3}>
        {description}
        {impact}
        {(isAuditFinding || isControlDeficiency) && rootCause}
        {isAuditFinding && recommendations}
      </Stack>
    </Stack>
  );
}
